/**
 * Control Integration Layer
 * Connects SDK Service to Zustand Store
 */
import { toast } from 'sonner';
import i18next from 'i18next';
import { useControlStore } from '../../store/control.store.js';
import { controlApi } from './control.api.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class ControlIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /**
     * List fetch matching the former store merge (no toast — inline page error).
     */
    async fetchControls(params = {}) {
        const store = useControlStore.getState();
        store.setLoading(true);
        store.clearError();

        try {
            const currentPagination = store.pagination;
            const effectiveColumnFilters = params.columnFilters !== undefined ? params.columnFilters : store.columnFilters || {};
            const effectiveScopeStandards = Array.isArray(params.scopeStandards)
                ? params.scopeStandards
                : store.scopeStandards || [];
            const effectiveCategoryDomains = Array.isArray(params.categoryDomains)
                ? params.categoryDomains
                : store.categoryDomains || [];
            const effectiveCatalogSeedTagsFilter = Array.isArray(params.catalogSeedTagsFilter)
                ? params.catalogSeedTagsFilter
                : store.catalogSeedTagsFilter || [];
            const response = await controlApi.getAll({
                ...params,
                page: params.page ?? currentPagination.page,
                pageSize: params.pageSize ?? currentPagination.pageSize,
                search: params.search ?? store.searchQuery,
                sortBy: params.sortBy ?? store.sortBy,
                sortOrder: params.sortOrder ?? store.sortOrder,
                columnFilters: effectiveColumnFilters,
                scopeStandards: effectiveScopeStandards,
                categoryDomains: effectiveCategoryDomains,
                catalogSeedTagsFilter: effectiveCatalogSeedTagsFilter,
            });
            const page = response.page;
            const totalPages = response.totalPages;
            useControlStore.setState({
                controls: response.data,
                pagination: {
                    page,
                    pageSize: response.pageSize,
                    total: response.total,
                    totalPages,
                    hasNextPage:
                        response.hasNextPage ?? (page < totalPages),
                    hasPreviousPage:
                        response.hasPreviousPage ?? (page > 1),
                },
                sortBy: params.sortBy ?? store.sortBy,
                sortOrder: params.sortOrder ?? store.sortOrder,
                columnFilters: effectiveColumnFilters,
                uniqueValues: response.uniqueValues || store.uniqueValues || {},
                scopeStandards: effectiveScopeStandards,
                categoryDomains: effectiveCategoryDomains,
                catalogSeedTagsFilter: effectiveCatalogSeedTagsFilter,
            });
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            store.setError(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async fetchControlById(id) {
        const store = useControlStore.getState();
        store.setLoading(true);

        try {
            const response = await this.service.getById(id);
            store.setSelectedControl(response.data);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async fetchScopeOptions() {
        const store = useControlStore.getState();
        store.setScopeLoading(true);
        store.clearError();
        try {
            const options = await controlApi.getScopeOptions();
            store.setScopeOptions(options);
            return options;
        } catch (error) {
            store.setError(error.message);
            return [];
        } finally {
            store.setScopeLoading(false);
        }
    }

    async fetchCategoryOptions() {
        const store = useControlStore.getState();
        store.setScopeLoading(true);
        store.clearError();
        try {
            const options = await controlApi.getCategoryOptions();
            store.setCategoryOptions(options);
            return options;
        } catch (error) {
            store.setError(error.message);
            return [];
        } finally {
            store.setScopeLoading(false);
        }
    }

    async fetchScopePreference() {
        const store = useControlStore.getState();
        store.setScopeLoading(true);
        store.clearError();
        try {
            const out = await controlApi.getScopePreference();
            const scope = Array.isArray(out?.scope_standards) ? out.scope_standards : [];
            const categories = Array.isArray(out?.category_domains) ? out.category_domains : [];
            const catalogTags = Array.isArray(out?.catalog_seed_tags_filter)
                ? out.catalog_seed_tags_filter
                : [];
            useControlStore.setState({
                scopeStandards: scope,
                categoryDomains: categories,
                catalogSeedTagsFilter: catalogTags,
            });
            return { scopeStandards: scope, categoryDomains: categories, catalogSeedTagsFilter: catalogTags };
        } catch (error) {
            store.setError(error.message);
            return { scopeStandards: [], categoryDomains: [], catalogSeedTagsFilter: [] };
        } finally {
            store.setScopeLoading(false);
        }
    }

    async createControl(data) {
        const store = useControlStore.getState();
        store.setLoading(true);

        try {
            const response = await this.service.create(data);
            toast.success(i18next.t('integrationToasts:controlCreated', { defaultValue: 'Control created' }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async updateControl(id, data) {
        const store = useControlStore.getState();
        store.setLoading(true);

        try {
            const response = await this.service.update(id, data);
            toast.success(i18next.t('integrationToasts:controlUpdated', { defaultValue: 'Control updated' }));
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async deleteControl(id) {
        const store = useControlStore.getState();
        store.setLoading(true);

        try {
            await this.service.delete(id);
            toast.success(i18next.t('integrationToasts:controlDeleted', { defaultValue: 'Control deleted' }));
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async getControlById(id) {
        try {
            const data = await controlApi.getById(id);
            return { success: true, data };
        } catch (error) {
            return { success: false, error: this._getErrorMessage(error) };
        }
    }

    async searchControls(params = {}) {
        try {
            const out = await controlApi.getAll(params);
            return { success: true, ...out };
        } catch (error) {
            const message = this._getErrorMessage(error);
            return { success: false, error: message, data: [] };
        }
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error);
    }
}
